import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Clock, Euro, TrendingUp, Plus, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    total_quotes: 0,
    pending_quotes: 0,
    total_revenue: 0
  });
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, quotesRes] = await Promise.all([
        axios.get(`${API_URL}/stats`),
        axios.get(`${API_URL}/quotes`)
      ]);
      setStats(statsRes.data);
      setRecentQuotes(quotesRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'brouillon': 'Brouillon',
      'envoyé': 'Envoyé',
      'en_cours': 'En cours',
      'validé': 'Validé',
      'en_production': 'En production',
      'terminé': 'Terminé',
      'annulé': 'Annulé'
    };
    return labels[status] || status;
  };

  const statCards = [
    {
      icon: FileText,
      label: "Total devis",
      value: stats.total_quotes,
      color: "#D4AF37"
    },
    {
      icon: Clock,
      label: "En attente",
      value: stats.pending_quotes,
      color: "#F59E0B"
    },
    {
      icon: Euro,
      label: "CA validé",
      value: `${stats.total_revenue?.toLocaleString('fr-FR')} €`,
      color: "#10B981"
    }
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-white">
          Bonjour, {user?.contact_name}
        </h1>
        <p className="text-white/50 mt-1">
          Bienvenue sur votre espace professionnel Granipal
        </p>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card-dashboard flex items-center gap-4"
          >
            <div 
              className="w-12 h-12 flex items-center justify-center"
              style={{ backgroundColor: `${stat.color}20`, border: `1px solid ${stat.color}30` }}
            >
              <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
            </div>
            <div>
              <p className="text-white/50 text-sm">{stat.label}</p>
              <p className="text-2xl font-bold text-white">{stat.value}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-2 gap-6">
        <Link
          to="/configurateur"
          className="card-dashboard flex items-center justify-between group hover:border-[#D4AF37]/50 transition-colors"
          data-testid="quick-new-quote"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#5D1F1F]/20 border border-[#5D1F1F]/30">
              <Plus className="w-6 h-6 text-[#5D1F1F]" />
            </div>
            <div>
              <p className="text-white font-semibold">Créer un nouveau devis</p>
              <p className="text-white/50 text-sm">Configurateur multi-étapes</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
        </Link>

        <Link
          to="/dashboard/devis"
          className="card-dashboard flex items-center justify-between group hover:border-[#D4AF37]/50 transition-colors"
          data-testid="quick-view-quotes"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/20 border border-[#D4AF37]/30">
              <FileText className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-white font-semibold">Voir tous mes devis</p>
              <p className="text-white/50 text-sm">Historique et suivi</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
        </Link>
      </div>

      {/* Recent Quotes */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Devis récents</h2>
          <Link 
            to="/dashboard/devis" 
            className="text-[#D4AF37] text-sm hover:underline"
          >
            Voir tout
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : recentQuotes.length === 0 ? (
          <div className="card-dashboard text-center py-12">
            <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/50">Aucun devis pour le moment</p>
            <Link to="/configurateur" className="btn-primary mt-4 inline-flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Créer votre premier devis
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/50 text-xs uppercase tracking-wider py-3 px-4">N° Devis</th>
                  <th className="text-left text-white/50 text-xs uppercase tracking-wider py-3 px-4">Client</th>
                  <th className="text-left text-white/50 text-xs uppercase tracking-wider py-3 px-4">Matière</th>
                  <th className="text-left text-white/50 text-xs uppercase tracking-wider py-3 px-4">Total TTC</th>
                  <th className="text-left text-white/50 text-xs uppercase tracking-wider py-3 px-4">Statut</th>
                  <th className="text-left text-white/50 text-xs uppercase tracking-wider py-3 px-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentQuotes.map((quote) => (
                  <tr 
                    key={quote.id} 
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  >
                    <td className="py-4 px-4">
                      <Link 
                        to={`/dashboard/devis/${quote.id}`}
                        className="text-[#D4AF37] hover:underline font-medium"
                      >
                        {quote.quote_number}
                      </Link>
                    </td>
                    <td className="py-4 px-4 text-white">{quote.client_info?.name}</td>
                    <td className="py-4 px-4 text-white/70">{quote.material_name}</td>
                    <td className="py-4 px-4 text-white font-medium">
                      {quote.total_ttc?.toLocaleString('fr-FR')} €
                    </td>
                    <td className="py-4 px-4">
                      <span className={`status-${quote.status} px-3 py-1 text-xs uppercase tracking-wider`}>
                        {getStatusLabel(quote.status)}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-white/50 text-sm">
                      {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
